import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { OlivesFamilies } from './olivesDesktop';
import './olives-details.css'
import {
    Box,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BackIcon from '../../../../../components/back/back';



export default function OlivesDetails() {
    // Mock useParams behavior for a runnable example
    const { name } = useParams();
    const [open, setOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    // Default to the first plant if no name is available for the demo
    const plantName = name || OlivesFamilies[0].name;

    const plant = useMemo(() => {
        if (!plantName) return null;
        const decodedName = decodeURIComponent(plantName);
        return OlivesFamilies.find(
            (item) => item.name.toLowerCase() === decodedName.toLowerCase()
        );
    }, [plantName]);

    if (!plant) {
        // Fallback for demo, or real app error
        return <Typography sx={{ p: 4 }}>Plant data not found for "{plantName}".</Typography>;
    }

    const handleImageClick = (src: any) => {
        setSelectedImage(src);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedImage(null);
    };

    // Separate the two groups of images for easier rendering logic
    const primaryImages = plant.gallery.filter((imgObj: any) => 'size' in imgObj);
    const secondaryImages = plant.gallery.filter((imgObj: any) => !('size' in imgObj));

    return (
        <div className="ficus-container">



            <BackIcon />
            <Box sx={{ textAlign: 'center', px: '1rem' }}>

                <div className=" title-overlay">
                    {/* <span className="flower">🍁</span> */}
                    <h1>{plant.name}</h1>
                    {/* <span className="flower">🍁</span> */}
                </div>

                {/* UPDATED DESCRIPTION BOX */}
                <Box
                    className="description-box-organic"
                    sx={{
                        maxWidth: '700px',
                        mx: 'auto',
                        mt: 4,
                        mb: 6,
                        p: 3, /* Increased padding for better look */
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: '0.8rem', md: '1.2rem' },
                            fontFamily: 'Century Gothic',
                            color: '#444'
                        }}
                    >
                        {plant.description}
                    </Typography>
                </Box>
            </Box>



            <Box sx={{ maxWidth: '1400px', margin: '0 auto', px: { xs: 2, md: 4 } }}>
                <Grid container spacing={4} sx={{ mt: 6 }} >

                    {/* 1. PRIMARY IMAGES (With Size) - Clear, Featured Cards */}
                    {primaryImages.length > 0 && (
                        <Grid item xs={12}>
                            <Typography
                                variant="h4"
                                sx={{ mb: 3, fontWeight: '700', textAlign: 'left', fontFamily: 'Century Gothic', color: '#8b4513', fontSize: "15px" }}
                            >
                                Available Sizes
                            </Typography>
                        </Grid>
                    )}

                    {primaryImages.map((imgObj: any, index: any) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={`with-size-${index}`}
                            className="gallery-item primary-card"
                            sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <Box
                                onClick={() => handleImageClick(imgObj.src)}
                                className="ficus-shape-card"
                                sx={{
                                    p: 2,
                                    bgcolor: '#F9F9F9',
                                    boxShadow: '0 8px 15px rgba(0, 0, 0, 0.15)',
                                    transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                                    cursor: 'pointer',
                                    width: '100%',
                                    maxWidth: '320px',
                                    '&:hover': {
                                        transform: 'translateY(-5px) scale(1.02)',
                                        boxShadow: '0 12px 20px rgba(0, 0, 0, 0.25)'
                                    },
                                }}
                            >
                                <img
                                    src={imgObj.src}
                                    alt={`${plant.name}-${index}`}
                                    style={{
                                        width: '100%',
                                        height: '18rem',
                                        objectFit: 'cover',
                                        borderRadius: '12px',
                                        marginBottom: '0.75rem',
                                    }}
                                // onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/320x288/8B9B7E/FFFFFF?text=Image+Error" }}
                                />
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    fontFamily="Playfair Display, serif"
                                    textAlign="center"
                                    color="#38761D"
                                >
                                    {("name" in imgObj && imgObj.name) ? imgObj.name : plant.name}
                                    <span style={{ color: "#CC5500", display: 'block', fontSize: '1rem', marginTop: '0.25rem' }}>
                                        SIZE: {imgObj.size?.toUpperCase()}
                                    </span>
                                </Typography>
                            </Box>
                        </Grid>
                    ))}

                    {/* 2. SECONDARY GALLERY IMAGES (No Size) - Asymmetric, Organic Shapes */}
                    {secondaryImages.length > 0 && (
                        <Grid item xs={12} sx={{ mt: 1, mb: 4 }}>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: '700', fontFamily: 'Century Gothic', color: '#8b4513', fontSize: "15px" }}
                            >
                                Detailed Views
                            </Typography>
                        </Grid>
                    )}

                    {secondaryImages.map((imgObj, index) => (
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            md={3}
                            key={`no-size-${index}`}
                            className="gallery-item secondary-card"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                // Apply random vertical offset for the asymmetric effect
                                // pt: index % 3 === 1 ? '50px !important' : '0px !important',
                                // pb: index % 3 === 2 ? '50px !important' : '0px !important',
                                alignItems: 'center',
                            }}
                        >
                            <img
                                onClick={() => handleImageClick(imgObj.src)}
                                src={imgObj.src}
                                alt={`${plant.name}-gallery-${index}`}
                                className="gallery-img secondary-shape"
                                // Apply a slight, random rotation on load
                                style={{ transform: `rotate(${index % 2 === 0 ? -2 : 2}deg)` }}
                            // onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/300x250/AABDAA/444444?text=Detail+Image+Error" }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FULL-SCREEN DIALOG MODAL */}
            <Dialog open={open} onClose={handleClose} maxWidth="lg" PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}>
                <DialogContent sx={{ position: 'relative', p: 0, overflow: 'hidden' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'fixed',
                            top: 16,
                            right: 16,
                            zIndex: 100,
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Full Size"
                            style={{
                                width: '100vw',
                                height: '100vh',
                                objectFit: 'contain',
                                display: 'block',
                                padding: '32px', /* Padding to avoid close button overlap */
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
